import { useEffect, useRef } from 'react';

/**
 * H3 — lightweight realtime "refresh signal" hook.
 *
 * Reuses the SINGLE dashboard WebSocket already mounted in DashboardLayout
 * (which re-emits every frame onto the `ws:dashboard` window event-bus) — so
 * this hook opens NO new socket. When a relevant business event arrives, it
 * calls the caller's existing API refetch. The WebSocket is only a signal;
 * the authoritative data always comes from the page's own API call.
 *
 * Storm-safe: bursts of related events are coalesced into ONE refetch per
 * debounce window. Events that arrive while the tab is hidden are deferred
 * and flushed once on return (focus / visibility), so background tabs and
 * APK/WebView resumes stay fresh without polling.
 *
 * @param {string[]} eventTypes  WS event `type`s that should trigger a refresh.
 * @param {Function} refreshFn   The page's existing refetch (called with no args).
 * @param {{debounceMs?: number}} [opts]
 */
export function useRealtimeRefresh(eventTypes, refreshFn, { debounceMs = 500 } = {}) {
  const fnRef = useRef(refreshFn);
  fnRef.current = refreshFn;
  const typesRef = useRef(eventTypes);
  typesRef.current = eventTypes;

  useEffect(() => {
    let timer = null;
    let dirty = false;

    const doRefresh = () => {
      timer = null;
      // Defer work for hidden tabs — flush on return instead (no background churn).
      if (typeof document !== 'undefined' && document.hidden) { dirty = true; return; }
      dirty = false;
      try { fnRef.current && fnRef.current(); } catch { /* ignore */ }
    };

    // Coalesce: while a refetch is pending, extra events are absorbed.
    const schedule = () => {
      if (timer) return;
      timer = setTimeout(doRefresh, debounceMs);
    };

    const onWsBus = (e) => {
      const t = e?.detail?.type;
      if (t && typesRef.current.includes(t)) schedule();
    };
    // Returning to the tab / resuming the app → re-fetch current authoritative data.
    const onVisible = () => { if (!document.hidden) schedule(); };
    const onFocus = () => { schedule(); };

    window.addEventListener('ws:dashboard', onWsBus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('ws:dashboard', onWsBus);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceMs]);
}
